import * as React from 'react'
import { castingStoreGetStepTitle, StoreProps } from '../../../../services/stores/casting.store'
import { I18nService } from '../../../../services/utils/i18n.service'
import PostalAddressInput from '../../../shared/postal-address-input.component'
import SelectableListComponent from '../../../shared/selectable-list.component'
import { InfoBox } from '../casting-styled.component'

type Props = StoreProps & {
  isQuotationDriven?: boolean
}

const i18nScoped = new I18nService(require('./casting-backoffice-service-delivery-form.component.yml'))

export function CastingBackofficeServiceDeliveryFormComponent ({ store, data, isQuotationDriven }: Props) {
  const { serviceDelivery, customer, city, backofficeFillServiceDelivery, requiredPlace, offeredService, appointmentRequiredRanges, market } = data
  const pickableAddresses = React.useMemo(() => {
    return customer.addresses.filter(_ => _.city && _.city === city.city || _.zipcode && _.zipcode === city.postalCode || requiredPlace !== 'customers_place')
  }, [city, customer.addresses, requiredPlace])

  const loadAddress = React.useCallback((address: Partial<StoreProps['data']['customer']['addresses'][0]>) => {
    const invoicingAddress = customer.invoicing_address || address
    store.set({
      serviceDelivery: {
        delivery_additional: requiredPlace === 'customers_place' && address.additional || '',
        delivery_city: requiredPlace === 'customers_place' && address.city || '',
        delivery_civility: requiredPlace === 'customers_place' ? { mr: 'delivery_mister' as const, ms: 'delivery_madam' as const }[customer.civility] : null,
        delivery_corporate_name: requiredPlace === 'customers_place' && customer.corporate_name || '',
        delivery_country: requiredPlace === 'customers_place' && address.country || market.coverage !== 'worldwide' && market.country || '',
        delivery_first_name: requiredPlace === 'customers_place' && customer.first_name || '',
        delivery_last_name: requiredPlace === 'customers_place' && customer.last_name || '',
        delivery_state: requiredPlace === 'customers_place' && address.state || '',
        delivery_street: requiredPlace === 'customers_place' && address.street || '',
        delivery_zipcode: requiredPlace === 'customers_place' && address.zipcode || '',
        delivery_address_name: requiredPlace === 'customers_place' && address.name || null,
        delivery_remember_address: false,
        billing_additional: invoicingAddress.additional || '',
        billing_city: invoicingAddress.city || '',
        billing_civility: { mr: 'billing_mister' as const, ms: 'billing_madam' as const }[customer.civility],
        billing_corporate_name: customer.corporate_name || '',
        billing_country: invoicingAddress.country || market.coverage !== 'worldwide' && market.country || '',
        billing_first_name: customer.first_name || '',
        billing_last_name: customer.last_name || '',
        billing_state: invoicingAddress.state || '',
        billing_street: invoicingAddress.street || '',
        billing_zipcode: invoicingAddress.zipcode || '',
        billing_same_address: !customer.corporate_name && requiredPlace === 'customers_place' && !customer.invoicing_address
      }
    })
  }, [customer.civility, customer.corporate_name, customer.first_name, customer.invoicing_address, customer.last_name, market.country, market.coverage, requiredPlace, store])
  
  const prefillAddressIfUniq = React.useCallback(() => {
    if (pickableAddresses.length <= 1) loadAddress(pickableAddresses[0] || {})
  }, [loadAddress, pickableAddresses])
  
  React.useEffect(() => {
    if (!serviceDelivery && backofficeFillServiceDelivery) prefillAddressIfUniq()
  }, [backofficeFillServiceDelivery, prefillAddressIfUniq, serviceDelivery])

  const resetServiceDelivery = React.useCallback(() => {
    store.set({ serviceDelivery: null, backofficeFillServiceDelivery: serviceDelivery && customer.addresses.length > 0 })
  }, [customer.addresses.length, serviceDelivery, store])

  const setSdState = React.useCallback((body: Partial<StoreProps['data']['serviceDelivery']>) => {
    if (serviceDelivery.billing_same_address) {
      for (const key in body) if (key.startsWith('delivery_')) (body as any)[`billing_${key.substring('delivery_'.length)}`] = (body as any)[key]
    }

    if (body.billing_same_address && !serviceDelivery.billing_same_address) {
      for (const key in serviceDelivery) if (key.startsWith('delivery_')) (body as any)[`billing_${key.substring('delivery_'.length)}`] = (serviceDelivery as any)[key]
    }
    
    if (body.billing_civility?.startsWith('delivery_')) body.billing_civility = body.billing_civility.replace('delivery_', 'billing_') as any

    store.set({ serviceDelivery: { ...serviceDelivery, ...body } })
  }, [serviceDelivery, store])

  const renderedAddressesChoices = React.useMemo(() => {
    return <>
      { customer.addresses.length > 0 && <p className='txtSmall'>{ i18nScoped.t('customer_has_many_addresses', { city: `${city.city}${(city.postalCode) ? (` (${city.postalCode})`) : ''}` }) }</p> }

      <SelectableListComponent
        name="castingAddress"
        data={ pickableAddresses }
        value={ null }
        onChange={ loadAddress }
        formatter={ item => <>
          { item.name && <div><b>{ item.name }</b></div> }
          { [item.street, item.city, item.zipcode].filter(_ => _).join(', ') }
          { item.additional && <div>{ item.additional }</div> }
        </> } />
    </>
  }, [city.city, city.postalCode, customer.addresses.length, loadAddress, pickableAddresses])
  
  const renderedServiceDeliveryForm = React.useMemo(() => {
    if (!serviceDelivery) return null
    
    return <>
      { requiredPlace === 'customers_place' && <>
        <p><b>{ i18nScoped.t('delivery_address') } { serviceDelivery.delivery_address_name && `| ${ serviceDelivery.delivery_address_name }` }</b></p>
        <PostalAddressInput
          hasIdentity
          hasAdditional
          isCorporate={ !!customer.corporate_name }
          name='delivery'
          prefix='delivery_'
          address={ serviceDelivery } 
          onChange={ body => setSdState({ ...body, delivery_address_name: null }) } />

        { pickableAddresses.length === 0 && <div className="inputCheckbox">
          <input id="delivery_remember_address" type="checkbox" checked={ serviceDelivery.delivery_remember_address } onChange={ event => setSdState({ delivery_remember_address: event.target.checked }) }/>
          <label htmlFor="delivery_remember_address">{ i18nScoped.t('remember_address') }</label>
        </div> }

        <div className="u-borderT u-pts u-mts u-mbs">
          <b>{ i18nScoped.t('billing_address') }</b>

          <div className="inputCheckbox" style={{ float: 'right' }}>
            <input id="same_address" type="checkbox" checked={ serviceDelivery.billing_same_address } onChange={ event => setSdState({ billing_same_address: event.target.checked }) }/>
            <label htmlFor="same_address">{ i18nScoped.t('billing_same_address') }</label>
          </div>
        </div>
      </> }

      { requiredPlace !== 'customers_place' && <div className="u-pts u-mts u-mbs">
        <b>{ i18nScoped.t('billing_address') }</b>
      </div> }

      { !serviceDelivery.billing_same_address && <PostalAddressInput
        hasIdentity
        hasAdditional
        isCorporate={ !!customer.corporate_name }
        prefix='billing_'
        name='billing'
        address={ serviceDelivery } 
        onChange={ setSdState } /> }
    </>
  }, [customer.corporate_name, pickableAddresses.length, requiredPlace, serviceDelivery, setSdState])

  const renderedOptForServiceDeliveryForm = React.useMemo(() => {
    if (backofficeFillServiceDelivery) return null

    const menuItemProps: React.HTMLAttributes<HTMLDivElement> = { className: 'boxPod', style: { margin: '0.5em', padding: '1em', cursor: 'pointer' } }

    return <>
      { !customer['has_any_valid_payment_mode?'] && <InfoBox children={ i18nScoped.t('customer_does_not_have_valid_payment_method') } /> }

      <div { ...menuItemProps } onClick={ () => { store.set({ backofficeFillServiceDelivery: false }); store.nextFocus({ closeOnMobile: true }) } } id="js-test-service-delivery-filled-by-customer">
        <p className='form-label u-mtn'>
          <em className='icon-long-arrow-right u-mrs' /> { i18nScoped.t('let_user_fill_service_delivery') }
        </p>

        { !isQuotationDriven && <p>{ i18nScoped.t('let_user_fill_service_delivery_tooltip') }</p> }
      </div>

      <div { ...menuItemProps } onClick={ () => { prefillAddressIfUniq(); store.set({ backofficeFillServiceDelivery: true }) } } id="js-test-service-delivery-filled-by-operator">
        <p className='form-label u-mtn'>
          <em className='icon-long-arrow-right u-mrs' />&nbsp;
          { i18nScoped.t(`operator_fills_service_delivery${
            isQuotationDriven 
            ? '_quotation.' + (
              offeredService.offered_service_type === 'product' 
                ? 'product'
                : requiredPlace === 'customers_place' 
                ? 'customers_place' 
                : 'other'
            ) 
            : ''
          }`) }
        </p>

        { !isQuotationDriven && <p>{ i18nScoped.t('operator_fills_service_delivery_tooltip') }</p> }
      </div>
    </>
  }, [backofficeFillServiceDelivery, customer, isQuotationDriven, offeredService.offered_service_type, prefillAddressIfUniq, requiredPlace, store])

  return <>
    { backofficeFillServiceDelivery && pickableAddresses.length > 0 && <h3 className="stepTooltip-title txtS u-gridInline-spaceBetween u-mbm">
      <span>
        <a 
          id="js-test-show-pickable-addresses" 
          className="icon-prev buttonNeutral button--XS u-mrm" 
          onClick={ () => resetServiceDelivery() } />

        { castingStoreGetStepTitle(data, 'backoffice-service-delivery') }
      </span>
    </h3> }

    { appointmentRequiredRanges !== null && requiredPlace === 'customers_place' && <InfoBox children={ i18nScoped.t('delivery_address_required_for_appointment') } /> }

    { !backofficeFillServiceDelivery
      ? renderedOptForServiceDeliveryForm
      : serviceDelivery
        ? renderedServiceDeliveryForm
        : renderedAddressesChoices
    }
  </>
}
